
// =========================================================
// DEBUTS CROP
// ProductsManager.jsx
// CATÁLOGO CENTRAL + ESCÁNER VISUAL
// PREMIUM NEON / RETRO TROPICAL
// =========================================================

import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Barcode,
  Camera,
  CheckCircle2,
  ChevronDown,
  Edit3,
  Eye,
  EyeOff,
  History,
  ImagePlus,
  LoaderCircle,
  Package,
  PackageCheck,
  PackagePlus,
  Plus,
  RefreshCw,
  ScanSearch,
  Search,
  ShieldCheck,
  Sparkles,
  Store,
  Tag,
  PackageSearch,
  Upload,
  Warehouse,
  X,
  XCircle,
} from "lucide-react";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import ProfilePageLayout from "../../components/profile/ProfilePageLayout";
import "../../styles/products-manager.css";

// ==========================================================
// D
// React + Django REST Framework
// Desarrollo y producción
// ==========================================================




const API_BASE_URL = (
  import.meta.env.VITE_API_URL ||
  (
    import.meta.env.PROD
      ? "https://api.debutscrop.com/api"
      : "http://localhost:8000/api"
  )
).replace(/\/+$/, "");


const PRODUCTS_ENDPOINT = "/products/";
const STORES_ENDPOINT = "/stores/";
const CATEGORIES_ENDPOINT = "/categories/";
const INVENTORY_ENDPOINT = "/inventory/";

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

const EMPTY_FORM = {
  store: "",
  category: "",
  name: "",
  description: "",
  sku: "",
  cost_price: "",
  profit_percentage: "30",
  price: "",
  compare_price: "",
  is_active: true,
  is_featured: false,
  original_image: null,
  image: null,
};

// =========================================================
// HELPERS
// =========================================================

function readAuth() {
  try {
    const raw = localStorage.getItem("auth");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readToken() {
  const auth = readAuth();

  return (
    auth?.access ||
    auth?.access_token ||
    auth?.token ||
    null
  );
}

function normalizeList(data, depth = 0) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object" || depth > 5) return [];

  const possibleLists = [
    data.results,
    data.data,
    data.items,
    data.stores,
    data.products,
    data.categories,
    data.inventory,
    data.records,
    data.payload,
  ];

  for (const value of possibleLists) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
      const nested = normalizeList(value, depth + 1);
      if (nested.length > 0) return nested;
    }
  }

  return [];
}

function money(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function number(value) {
  return new Intl.NumberFormat("es-CO").format(
    Number(value || 0)
  );
}

function imageUrl(value) {
  if (!value) return "";

  if (typeof value === "object") {
    value = value.url || value.image || value.src || "";
  }

  if (!value || typeof value !== "string") return "";

  if (/^https?:\/\//i.test(value)) return value;

  if (value.startsWith("/")) {
    try {
      return `${new URL(API_BASE_URL).origin}${value}`;
    } catch {
      return value;
    }
  }

  return value;
}

function getErrorMessage(data, fallback) {
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.message === "string") return data.message;
  if (typeof data === "string" && data.trim()) return data;

  if (data && typeof data === "object") {
    const key = Object.keys(data)[0];
    const value = key ? data[key] : null;

    if (Array.isArray(value) && value.length) {
      return `${key}: ${value[0]}`;
    }

    if (typeof value === "string") {
      return `${key}: ${value}`;
    }
  }

  return fallback;
}

function getProductId(product) {
  return product?.id ?? product?.pk ?? null;
}

function getProductName(product) {
  return product?.name || product?.product_name || "Producto sin nombre";
}

function getProductImage(product) {
  return (
    imageUrl(product?.image) ||
    imageUrl(product?.catalog_image) ||
    imageUrl(product?.primary_image) ||
    imageUrl(product?.catalog_images?.[0]?.image) ||
    imageUrl(product?.catalog_gallery?.[0]?.image) ||
    ""
  );
}

function getCategoryName(product, categories) {
  if (product?.category_name) return product.category_name;

  const id =
    typeof product?.category === "object"
      ? product.category?.id
      : product?.category;

  return categories.find(
    (category) => String(category.id ?? category.pk) === String(id)
  )?.name || "Sin categoría";
}

function getStoreName(product, stores) {
  if (product?.store_name) return product.store_name;

  const id =
    typeof product?.store === "object"
      ? product.store?.id
      : product?.store;

  const found = stores.find(
    (store) => String(store.id ?? store.pk) === String(id)
  );

  return (
    found?.name ||
    found?.store_name ||
    found?.business_name ||
    found?.title ||
    `Tienda #${id || "—"}`
  );
}

function getInventoryForProduct(product, inventory) {
  const productId = getProductId(product);

  return inventory.find((item) => {
    const id =
      typeof item.product === "object"
        ? item.product?.id
        : item.product;

    return String(id) === String(productId);
  }) || null;
}

function getStockValues(product, inventory) {
  const record = getInventoryForProduct(product, inventory);

  const quantity = Number(
    record?.quantity ??
    product?.inventory?.quantity ??
    0
  );

  const reserved = Number(
    record?.reserved_quantity ??
    record?.reserved_stock ??
    product?.inventory?.reserved_quantity ??
    product?.reserved_stock ??
    0
  );

  const available = Number(
    record?.available_quantity ??
    product?.available_stock ??
    Math.max(0, quantity - reserved)
  );

  const low = Boolean(
    record?.low_stock ??
    product?.is_low_stock ??
    false
  );

  return {
    quantity,
    reserved,
    available,
    low,
    empty: Boolean(record?.out_of_stock) || available <= 0,
  };
}

function validateImage(file) {
  if (!file) return "Selecciona una imagen.";

  const allowed = [
    "image/jpeg",
    "image/png",
    "image/webp",
  ];

  if (!allowed.includes(file.type)) {
    return "Selecciona una imagen JPG, PNG o WEBP.";
  }

  if (file.size > MAX_IMAGE_SIZE) {
    return "La imagen no puede superar los 10 MB.";
  }

  return "";
}

// =========================================================
// COMPONENTE
// =========================================================

export default function ProductsManager() {
  const navigate = useNavigate();

  const originalInputRef = useRef(null);
  const catalogInputRef = useRef(null);
  const scannerInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [activeTab, setActiveTab] = useState("catalog");

  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [inventory, setInventory] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingStatus, setChangingStatus] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [storesError, setStoresError] = useState("");
  const [loadingStores, setLoadingStores] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [storeFilter, setStoreFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);

  const [previews, setPreviews] = useState({
    original_image: "",
    image: "",
  });

  // =======================================================
  // ESCÁNER
  // =======================================================

  const [scannerStore, setScannerStore] = useState("");
  const [scannerImage, setScannerImage] = useState(null);
  const [scannerPreview, setScannerPreview] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [scannerMessage, setScannerMessage] = useState("");
  const [scannerError, setScannerError] = useState("");
  const [barcodeValue, setBarcodeValue] = useState("");
  const [scannerSearch, setScannerSearch] = useState("");
  const [scannerCandidates, setScannerCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
 
  // =======================================================
  // API REQUEST
  // =======================================================

  const apiRequest = useCallback(async (endpoint, options = {}) => {
    const token = readToken();

    if (!token) {
      navigate("/login", { replace: true });
      throw new Error(
        "Debes iniciar sesión para administrar productos."
      );
    }

    const headers = {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };

    if (
      options.body &&
      !(options.body instanceof FormData) &&
      !headers["Content-Type"]
    ) {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(
      `${API_BASE_URL}${endpoint}`,
      {
        ...options,
        headers,
      }
    );

    if (response.status === 401) {
      localStorage.removeItem("auth");
      localStorage.removeItem("user");
      window.dispatchEvent(new Event("authUpdated"));
      navigate("/login", { replace: true });

      throw new Error(
        "Tu sesión expiró. Inicia sesión nuevamente."
      );
    }

    const contentType = response.headers.get("content-type") || "";
    let data = null;

    if (response.status !== 204) {
      if (contentType.includes("application/json")) {
        data = await response.json().catch(() => null);
      } else {
        data = await response.text().catch(() => "");
      }
    }

    if (!response.ok) {
      throw new Error(
        getErrorMessage(
          data,
          `Error ${response.status} al consultar ${endpoint}.`
        )
      );
    }

    return data;
  }, [navigate]);

  // =======================================================
  // LOADERS
  // =======================================================

  const loadProducts = useCallback(async () => {
    const data = await apiRequest(PRODUCTS_ENDPOINT);
    setProducts(normalizeList(data));
  }, [apiRequest]);

  const loadStores = useCallback(async () => {
    setLoadingStores(true);
    setStoresError("");

    try {
      const data = await apiRequest(STORES_ENDPOINT);
      const list = normalizeList(data);
      const validStores = list.filter((store) => {
        const id = store?.id ?? store?.pk;
        return id !== null && id !== undefined && String(id).trim() !== "";
      });

      setStores(validStores);

      if (validStores.length === 0) {
        setStoresError(
          "La API respondió, pero no devolvió tiendas disponibles. Verifica GET /api/stores/, los permisos del usuario y que existan tiendas registradas en Django."
        );
      }
    } catch (requestError) {
      console.error("[DEBUTS CROP] Error cargando tiendas:", requestError);
      setStores([]);
      setStoresError(
        requestError?.message ||
        "No fue posible cargar las tiendas. Verifica el endpoint /api/stores/ y los permisos del usuario."
      );
    } finally {
      setLoadingStores(false);
    }
  }, [apiRequest]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await apiRequest(CATEGORIES_ENDPOINT);
      setCategories(normalizeList(data));
    } catch {
      setCategories([]);
    }
  }, [apiRequest]);

  const loadInventory = useCallback(async () => {
    try {
      const data = await apiRequest(INVENTORY_ENDPOINT);
      setInventory(normalizeList(data));
    } catch {
      setInventory([]);
    }
  }, [apiRequest]);

  const loadAll = useCallback(async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");
    setNotice("");

    try {
      await Promise.all([
        loadProducts(),
        loadStores(),
        loadCategories(),
        loadInventory(),
      ]);
    } catch (requestError) {
      setError(
        requestError?.message ||
        "No fue posible cargar el catálogo de productos."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    loadProducts,
    loadStores,
    loadCategories,
    loadInventory,
  ]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  
  // =======================================================
  // DATOS DERIVADOS
  // =======================================================

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();

    return products.filter((product) => {
      const text = [
        product.name,
        product.sku,
        product.barcode,
        product.category_name,
        product.store_name,
        product.description,
        product.id,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLowerCase();

      if (query && !text.includes(query)) return false;

      if (statusFilter === "active" && !product.is_active) return false;
      if (statusFilter === "inactive" && product.is_active) return false;
      if (statusFilter === "featured" && !product.is_featured) return false;

      const productStore =
        typeof product.store === "object"
          ? product.store?.id
          : product.store;

      if (
        storeFilter !== "all" &&
        String(productStore) !== String(storeFilter)
      ) {
        return false;
      }

      const productCategory =
        typeof product.category === "object"
          ? product.category?.id
          : product.category;

      if (
        categoryFilter !== "all" &&
        String(productCategory) !== String(categoryFilter)
      ) {
        return false;
      }

      return true;
    });
  }, [
    products,
    search,
    statusFilter,
    storeFilter,
    categoryFilter,
  ]);

  const summary = useMemo(() => {
    const active = products.filter(
      (product) => Boolean(product.is_active)
    ).length;

    const featured = products.filter(
      (product) => Boolean(product.is_featured)
    ).length;

    const stocks = products.map(
      (product) => getStockValues(product, inventory)
    );

    const lowStock = stocks.filter(
      (stock) => stock.low && !stock.empty
    ).length;

    const outOfStock = stocks.filter(
      (stock) => stock.empty
    ).length;

    return {
      total: products.length,
      active,
      inactive: products.length - active,
      featured,
      lowStock,
      outOfStock,
    };
  }, [products, inventory]);

  const calculatedPrice = useMemo(() => {
    const cost = Number(form.cost_price || 0);
    const profit = Number(form.profit_percentage || 0);

    if (!Number.isFinite(cost) || !Number.isFinite(profit)) {
      return 0;
    }

    return Math.round(cost * (1 + profit / 100));
  }, [form.cost_price, form.profit_percentage]);

  // =======================================================
  // FORMULARIO PRODUCTO
  // =======================================================

  const resetPreviews = useCallback(() => {
    setPreviews((previous) => {
      Object.values(previous).forEach((value) => {
        if (value?.startsWith("blob:")) URL.revokeObjectURL(value);
      });

      return {
        original_image: "",
        image: "",
      };
    });
  }, []);

  const openCreateForm = () => {
    resetPreviews();
    setEditingProduct(null);

    setForm({
      ...EMPTY_FORM,
      store: stores.length === 1 ? String(stores[0].id ?? stores[0].pk) : "",
    });

    setError("");
    setNotice("");
    setShowForm(true);
    setActiveTab("form");
  };

  const openEditForm = (product) => {
    resetPreviews();

    const storeId =
      typeof product.store === "object"
        ? product.store?.id
        : product.store;

    const categoryId =
      typeof product.category === "object"
        ? product.category?.id
        : product.category;

    setEditingProduct(product);

    setForm({
      ...EMPTY_FORM,
      store: storeId ? String(storeId) : "",
      category: categoryId ? String(categoryId) : "",
      name: product.name || "",
      description: product.description || "",
      sku: product.sku || "",
      cost_price: product.cost_price ?? "",
      profit_percentage: product.profit_percentage ?? "30",
      price: product.price ?? "",
      compare_price: product.compare_price ?? "",
      is_active: Boolean(product.is_active),
      is_featured: Boolean(product.is_featured),
      original_image: null,
      image: null,
    });

    setPreviews({
      original_image: imageUrl(product.original_image),
      image: getProductImage(product),
    });

    setError("");
    setNotice("");
    setShowForm(true);
    setActiveTab("form");
  };

  const closeForm = () => {
    if (saving) return;

    setShowForm(false);
    setEditingProduct(null);
    setForm(EMPTY_FORM);
    resetPreviews();
    setActiveTab("catalog");
  };

  const handleImageChange = (event, field) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateImage(file);

    if (validationError) {
      setError(validationError);
      event.target.value = "";
      return;
    }

    setError("");

    setForm((previous) => ({
      ...previous,
      [field]: file,
    }));

    setPreviews((previous) => {
      if (previous[field]?.startsWith("blob:")) {
        URL.revokeObjectURL(previous[field]);
      }

      return {
        ...previous,
        [field]: URL.createObjectURL(file),
      };
    });
  };

  const saveProduct = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      setError("El nombre del producto es obligatorio.");
      return;
    }

    if (!form.store) {
      setError("Selecciona una tienda para el producto.");
      return;
    }

    if (form.cost_price === "" || Number(form.cost_price) < 0) {
      setError("Ingresa un costo válido, igual o superior a cero.");
      return;
    }

    if (
      form.profit_percentage === "" ||
      Number(form.profit_percentage) < 0
    ) {
      setError("Ingresa un porcentaje de ganancia válido.");
      return;
    }

    const payload = new FormData();

    payload.append("store", String(form.store));
    payload.append("name", form.name.trim());
    payload.append("description", form.description || "");
    payload.append("cost_price", String(Number(form.cost_price)));
    payload.append(
      "profit_percentage",
      String(Number(form.profit_percentage))
    );
    payload.append("is_active", String(Boolean(form.is_active)));
    payload.append("is_featured", String(Boolean(form.is_featured)));

    if (form.category) payload.append("category", String(form.category));
    if (form.sku.trim()) payload.append("sku", form.sku.trim());

    if (form.compare_price !== "" && form.compare_price !== null) {
      payload.append("compare_price", String(Number(form.compare_price)));
    }

    if (form.original_image instanceof File) {
      payload.append("original_image", form.original_image);
    }

    if (form.image instanceof File) {
      payload.append("image", form.image);
    }

    const productId = getProductId(editingProduct);
    const endpoint = productId
      ? `${PRODUCTS_ENDPOINT}${productId}/`
      : PRODUCTS_ENDPOINT;

    setSaving(true);
    setError("");
    setNotice("");

    try {
      const result = await apiRequest(endpoint, {
        method: productId ? "PATCH" : "POST",
        body: payload,
      });

      setNotice(
        productId
          ? "Producto actualizado correctamente."
          : "Producto creado correctamente."
      );

      setShowForm(false);
      setEditingProduct(null);
      setForm(EMPTY_FORM);
      resetPreviews();
      setActiveTab("catalog");

      await Promise.all([loadProducts(), loadInventory()]);

      if (result && getProductId(result)) {
        setSelectedProduct(result);
      }
    } catch (requestError) {
      setError(
        requestError?.message ||
        "No fue posible guardar el producto."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleProductStatus = async (product) => {
    const productId = getProductId(product);
    if (!productId) return;

    setChangingStatus(productId);
    setError("");
    setNotice("");

    try {
      await apiRequest(`${PRODUCTS_ENDPOINT}${productId}/`, {
        method: "PATCH",
        body: JSON.stringify({
          is_active: !product.is_active,
        }),
      });

      setNotice(
        product.is_active
          ? "Producto desactivado correctamente."
          : "Producto activado correctamente."
      );

      await loadProducts();
    } catch (requestError) {
      setError(
        requestError?.message ||
        "No fue posible cambiar el estado del producto."
      );
    } finally {
      setChangingStatus(null);
    }
  };

  // =======================================================
  // ESCÁNER: CARGA DE IMAGEN
  // =======================================================

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setCameraOpen(false);
    setCameraLoading(false);
  }, []);

  const clearScannerImage = () => {
    if (scannerPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(scannerPreview);
    }

    setScannerImage(null);
    setScannerPreview("");
    setScannerMessage("");
    setScannerError("");
    setBarcodeValue("");
    setScannerCandidates([]);
    setSelectedCandidate(null);
  };

  const handleScannerFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateImage(file);

    if (validationError) {
      setScannerError(validationError);
      event.target.value = "";
      return;
    }

    if (scannerPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(scannerPreview);
    }

    setScannerImage(file);
    setScannerPreview(URL.createObjectURL(file));
    setScannerMessage(
      "Imagen cargada. Selecciona la tienda y revisa los productos candidatos."
    );
    setScannerError("");
    setScannerCandidates([]);
    setSelectedCandidate(null);
    setBarcodeValue("");
  };

  // =======================================================
  // ESCÁNER: CÁMARA
  // =======================================================

  const openCamera = async () => {
    setScannerError("");
    setScannerMessage("");
    setCameraLoading(true);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraLoading(false);
      setScannerError(
        "Este navegador no permite acceder a la cámara. Usa HTTPS o localhost, o carga una fotografía."
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;
      setCameraOpen(true);

      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      });
    } catch (cameraError) {
      setScannerError(
        cameraError?.name === "NotAllowedError"
          ? "No se concedió permiso para usar la cámara."
          : "No fue posible abrir la cámara. Revisa el dispositivo o carga una imagen."
      );
    } finally {
      setCameraLoading(false);
    }
  };

  const captureCameraImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      setScannerError("La cámara todavía no está lista.");
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext("2d");

    if (!context) {
      setScannerError("No se pudo procesar la captura.");
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        setScannerError("No se pudo generar la imagen capturada.");
        return;
      }

      const file = new File(
        [blob],
        `debutscrop-scan-${Date.now()}.jpg`,
        { type: "image/jpeg" }
      );

      if (scannerPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(scannerPreview);
      }

      setScannerImage(file);
      setScannerPreview(URL.createObjectURL(file));
      setScannerMessage(
        "Fotografía capturada. La coincidencia visual requiere un servicio de escaneo conectado al backend."
      );
      setScannerError("");
      setScannerCandidates([]);
      setSelectedCandidate(null);
      setBarcodeValue("");

      stopCamera();
    }, "image/jpeg", 0.92);
  };

  // =======================================================
  // ESCÁNER: CANDIDATOS LOCALES
  //
  // Este filtro ayuda a revisar el catálogo existente.
  // No simula reconocimiento visual ni escribe ProductScan.
  // =======================================================

  const localCandidates = useMemo(() => {
    const query = scannerSearch.trim().toLowerCase();

    return products.filter((product) => {
      const productStore =
        typeof product.store === "object"
          ? product.store?.id
          : product.store;

      if (
        scannerStore &&
        String(productStore) !== String(scannerStore)
      ) {
        return false;
      }

      if (!query) return true;

      const text = [
        product.name,
        product.sku,
        product.barcode,
        product.description,
        product.id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(query);
    }).slice(0, 30);
  }, [products, scannerStore, scannerSearch]);

  const searchLocalCandidates = () => {
    setScannerError("");
    setScannerCandidates(localCandidates);
    setSelectedCandidate(null);

    if (!scannerImage) {
      setScannerError("Primero captura o carga la imagen que quieres revisar.");
      return;
    }

    if (!scannerStore) {
      setScannerError("Selecciona la tienda para limitar la búsqueda.");
      return;
    }

    if (!scannerSearch.trim()) {
      setScannerMessage(
        "Escribe el nombre, SKU o código que aparece en el producto para buscar candidatos."
      );
      return;
    }

    setScannerMessage(
      `${localCandidates.length} candidato(s) encontrados por texto. Esto no es reconocimiento automático de imagen.`
    );

    setScanHistory((previous) => [
      {
        id: `${Date.now()}`,
        date: new Date().toISOString(),
        fileName: scannerImage.name,
        store: stores.find(
          (store) => String(store.id) === String(scannerStore)
        )?.name || `Tienda #${scannerStore}`,
        query: scannerSearch.trim(),
        results: localCandidates.length,
        status: "Revisión manual",
      },
      ...previous,
    ].slice(0, 20));
  };

  const selectScannerCandidate = (product) => {
    setSelectedCandidate(product);
    setScannerMessage(
      `Seleccionaste "${getProductName(product)}". La selección todavía no modifica el producto ni el inventario.`
    );
  };

  const openScannerTab = () => {
    setShowForm(false);
    setActiveTab("scanner");
    setScannerError("");
    setScannerMessage("");
    setScannerStore((previous) => (
      previous || (stores.length === 1 ? String(stores[0].id ?? stores[0].pk) : "")
    ));
  };

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <ProfilePageLayout
      title="Productos"
      subtitle="Catálogo, precios, publicación, imágenes y escáner de Debuts Crop."
      icon={Package}
    >
      <main className="dc-products-page">
        <button
          type="button"
          className="dc-products-back"
          onClick={() => navigate("/profile")}
        >
          <ArrowLeft size={17} />
          VOLVER A MI PERFIL
        </button>

        {/* HERO */}

        <section className="dc-products-hero">
          <div className="dc-products-hero-copy">
            <span className="dc-products-eyebrow">
              DEBUTS CROP · PRODUCT COMMAND CENTER
            </span>

            <h1>
              GESTIÓN DE
              <span> PRODUCTOS</span>
            </h1>

            <p>
              Administra el catálogo, controla la publicación, organiza las imágenes
              y prepara la revisión de productos mediante el escáner.
            </p>

            <div className="dc-products-hero-actions">
              <button
                type="button"
                className="dc-products-primary"
                onClick={openCreateForm}
              >
                <PackagePlus size={17} />
                NUEVO PRODUCTO
              </button>

              <button
                type="button"
                className="dc-products-secondary"
                onClick={openScannerTab}
              >
                <ScanSearch size={17} />
                ABRIR ESCÁNER
              </button>

              <button
                type="button"
                className="dc-products-secondary"
                onClick={() => loadAll(true)}
                disabled={refreshing}
              >
                {refreshing
                  ? <LoaderCircle size={17} className="dc-products-spin" />
                  : <RefreshCw size={17} />}
                ACTUALIZAR
              </button>
            </div>
          </div>

          <div className="dc-products-hero-art">
            <div className="dc-products-orbit dc-products-orbit-one" />
            <div className="dc-products-orbit dc-products-orbit-two" />

            <div className="dc-products-hero-icon">
              <PackageSearchIcon />
            </div>

            <span className="dc-products-floating dc-products-float-a">
              <Barcode size={17} /> SKU / QR
            </span>

            <span className="dc-products-floating dc-products-float-b">
              <Sparkles size={17} /> CATÁLOGO
            </span>
          </div>
        </section>

        {/* ALERTAS */}

        {error && (
          <div className="dc-products-alert error" role="alert">
            <XCircle size={19} />
            <span>{error}</span>
            <button type="button" onClick={() => setError("")} aria-label="Cerrar error">
              <X size={17} />
            </button>
          </div>
        )}

        {notice && (
          <div className="dc-products-alert success" role="status">
            <CheckCircle2 size={19} />
            <span>{notice}</span>
            <button type="button" onClick={() => setNotice("")} aria-label="Cerrar aviso">
              <X size={17} />
            </button>
          </div>
        )}

        {storesError && (
          <div className="dc-products-alert error" role="alert">
            <XCircle size={19} />
            <span>
              <strong>Problema al cargar las tiendas:</strong>{" "}
              {storesError}
            </span>
            <button
              type="button"
              onClick={loadStores}
              disabled={loadingStores}
              aria-label="Volver a cargar tiendas"
              title="Volver a cargar tiendas"
            >
              {loadingStores
                ? <LoaderCircle size={17} className="dc-products-spin" />
                : <RefreshCw size={17} />}
            </button>
          </div>
        )}

        {/* INDICADORES */}

        <section className="dc-products-stats">
          <article className="dc-products-stat">
            <div className="dc-products-stat-icon purple"><Package size={21} /></div>
            <span>PRODUCTOS REGISTRADOS</span>
            <strong>{number(summary.total)}</strong>
            <small>Catálogo central</small>
          </article>

          <article className="dc-products-stat">
            <div className="dc-products-stat-icon green"><CheckCircle2 size={21} /></div>
            <span>PRODUCTOS ACTIVOS</span>
            <strong>{number(summary.active)}</strong>
            <small>Habilitados en el catálogo</small>
          </article>

          <article className="dc-products-stat">
            <div className="dc-products-stat-icon blue"><Sparkles size={21} /></div>
            <span>PRODUCTOS DESTACADOS</span>
            <strong>{number(summary.featured)}</strong>
            <small>Marcados como destacados</small>
          </article>

          <article className="dc-products-stat warning">
            <div className="dc-products-stat-icon orange"><AlertTriangle size={21} /></div>
            <span>ALERTAS DE INVENTARIO</span>
            <strong>{number(summary.lowStock + summary.outOfStock)}</strong>
            <small>
              {number(summary.lowStock)} bajo · {number(summary.outOfStock)} agotado
            </small>
          </article>
        </section>

        {/* PESTAÑAS */}

        <nav className="dc-products-tabs" aria-label="Secciones de productos">
          <button
            type="button"
            className={activeTab === "catalog" ? "active" : ""}
            onClick={() => {
              setActiveTab("catalog");
              setShowForm(false);
            }}
          >
            <Package size={17} /> CATÁLOGO
          </button>

          <button
            type="button"
            className={activeTab === "form" ? "active" : ""}
            onClick={() => {
              if (!showForm) openCreateForm();
            }}
          >
            <Plus size={17} /> NUEVO PRODUCTO
          </button>

          <button
            type="button"
            className={activeTab === "inventory" ? "active" : ""}
            onClick={() => {
              setActiveTab("inventory");
              setShowForm(false);
            }}
          >
            <Warehouse size={17} /> INVENTARIO
          </button>

          <button
            type="button"
            className={activeTab === "scanner" ? "active" : ""}
            onClick={openScannerTab}
          >
            <ScanSearch size={17} /> ESCÁNER
          </button>
        </nav>

        {loading ? (
          <section className="dc-products-panel dc-products-loading">
            <LoaderCircle size={30} className="dc-products-spin" />
            <strong>CARGANDO CATÁLOGO...</strong>
            <span>Conectando con el backend de Debuts Crop</span>
          </section>
        ) : (
          <>
            {/* =================================================
                ESCÁNER VISUAL
            ================================================= */}

            {activeTab === "scanner" && (
              <section className="dc-products-panel dc-products-scanner-panel">
                <div className="dc-products-panel-heading">
                  <div>
                    <span className="dc-products-eyebrow">PRODUCT SCAN CENTER</span>
                    <h2>Escáner de productos</h2>
                    <p>
                      Captura la etiqueta o fotografía del producto y revisa posibles
                      coincidencias del catálogo.
                    </p>
                  </div>

                  <span className="dc-products-total-badge">
                    <ScanSearch size={15} /> REVISIÓN MANUAL
                  </span>
                </div>

                <div className="dc-products-inline-warning">
                  <AlertTriangle size={18} />
                  <span>
                    La cámara y la carga de imágenes están habilitadas en este template.
                    El reconocimiento automático y el registro persistente de ProductScan
                    necesitan un endpoint de escaneo conectado en Django. Esta pantalla
                    no inventa resultados ni modifica inventario.
                  </span>
                </div>

                <div className="dc-products-scanner-layout">
                  <div className="dc-products-scanner-capture">
                    <div className="dc-products-form-section-title">
                      <Camera size={19} />
                      <div>
                        <h3>Captura de referencia</h3>
                        <p>Usa la cámara o carga una imagen existente.</p>
                      </div>
                    </div>

                    <div className="dc-products-scanner-preview">
                      {scannerPreview ? (
                        <img src={scannerPreview} alt="Imagen seleccionada para escaneo" />
                      ) : (
                        <div className="dc-products-scanner-empty">
                          <ScanSearch size={45} />
                          <strong>ESPERANDO IMAGEN</strong>
                          <span>La fotografía aparecerá aquí.</span>
                        </div>
                      )}
                    </div>

                    <div className="dc-products-scanner-actions">
                      <button
                        type="button"
                        className="dc-products-primary"
                        onClick={openCamera}
                        disabled={cameraLoading}
                      >
                        {cameraLoading
                          ? <LoaderCircle size={17} className="dc-products-spin" />
                          : <Camera size={17} />}
                        ABRIR CÁMARA
                      </button>

                      <button
                        type="button"
                        className="dc-products-secondary"
                        onClick={() => scannerInputRef.current?.click()}
                      >
                        <Upload size={17} /> CARGAR IMAGEN
                      </button>

                      <button
                        type="button"
                        className="dc-products-secondary"
                        onClick={clearScannerImage}
                        disabled={!scannerImage}
                      >
                        <X size={16} /> LIMPIAR
                      </button>

                      <input
                        ref={scannerInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        capture="environment"
                        className="dc-products-hidden-input"
                        onChange={handleScannerFile}
                      />

                      <canvas ref={canvasRef} className="dc-products-hidden-input" />
                    </div>

                    {scannerImage && (
                      <div className="dc-products-scanner-file">
                        <CheckCircle2 size={16} />
                        <span>{scannerImage.name}</span>
                        <small>{(scannerImage.size / 1024).toFixed(0)} KB</small>
                      </div>
                    )}
                  </div>

                  <div className="dc-products-scanner-review">
                    <div className="dc-products-form-section-title">
                      <PackageSearch size={19} />
                      <div>
                        <h3>Buscar candidatos</h3>
                        <p>Filtra por tienda, nombre, SKU o código conocido.</p>
                      </div>
                    </div>

                    <label className="dc-products-field">
                      <span>TIENDA DEL PRODUCTO *</span>
                      <select
                        value={scannerStore}
                        onChange={(event) => setScannerStore(event.target.value)}
                      >
                        <option value="">Seleccionar tienda</option>
                        {stores.map((store) => {
                          const storeId = store.id ?? store.pk;
                          const storeName =
                            store.name ||
                            store.store_name ||
                            store.business_name ||
                            store.title ||
                            `Tienda #${storeId}`;

                          return (
                            <option key={String(storeId)} value={String(storeId)}>
                              {storeName}
                            </option>
                          );
                        })}
                      </select>
                    </label>

                    <label className="dc-products-field">
                      <span>NOMBRE, SKU O CÓDIGO</span>
                      <input
                        type="search"
                        value={scannerSearch}
                        onChange={(event) => setScannerSearch(event.target.value)}
                        placeholder="Ej. PRD-000001 o nombre del producto"
                      />
                    </label>

                    <label className="dc-products-field">
                      <span>CÓDIGO LEÍDO (OPCIONAL)</span>
                      <input
                        type="text"
                        value={barcodeValue}
                        onChange={(event) => {
                          setBarcodeValue(event.target.value);
                          setScannerSearch(event.target.value);
                        }}
                        placeholder="Introduce el código de barras o QR"
                      />
                    </label>

                    <button
                      type="button"
                      className="dc-products-primary"
                      onClick={searchLocalCandidates}
                      disabled={!scannerImage}
                    >
                      <Search size={17} /> BUSCAR EN EL CATÁLOGO
                    </button>

                    {scannerError && (
                      <div className="dc-products-alert error" role="alert">
                        <XCircle size={17} />
                        <span>{scannerError}</span>
                      </div>
                    )}

                    {scannerMessage && (
                      <div className="dc-products-alert success" role="status">
                        <Activity size={17} />
                        <span>{scannerMessage}</span>
                      </div>
                    )}

                    {selectedCandidate && (
                      <div className="dc-products-scanner-selected">
                        <CheckCircle2 size={19} />
                        <div>
                          <strong>PRODUCTO SELECCIONADO</strong>
                          <span>{getProductName(selectedCandidate)}</span>
                          <small>
                            SKU: {selectedCandidate.sku || "Sin SKU"} · ID: {getProductId(selectedCandidate)}
                          </small>
                        </div>
                        <button
                          type="button"
                          className="dc-products-secondary"
                          onClick={() => {
                            setSelectedProduct(selectedCandidate);
                          }}
                        >
                          <Eye size={15} /> VER
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="dc-products-scanner-results">
                  <div className="dc-products-panel-heading">
                    <div>
                      <span className="dc-products-eyebrow">CANDIDATE REVIEW</span>
                      <h2>Productos candidatos</h2>
                      <p>
                        Los resultados de esta plantilla son filtros por texto del catálogo,
                        no coincidencias visuales generadas por IA.
                      </p>
                    </div>
                    <span className="dc-products-total-badge">
                      {scannerCandidates.length} RESULTADOS
                    </span>
                  </div>

                  {scannerCandidates.length === 0 ? (
                    <div className="dc-products-empty">
                      <PackageSearchIcon />
                      <strong>NO HAY CANDIDATOS PARA REVISAR</strong>
                      <span>
                        Carga una imagen, selecciona una tienda y busca por nombre, SKU o código.
                      </span>
                    </div>
                  ) : (
                    <div className="dc-products-scanner-candidates">
                      {scannerCandidates.map((product) => {
                        const selected =
                          String(getProductId(selectedCandidate)) ===
                          String(getProductId(product));

                        return (
                          <article
                            key={getProductId(product)}
                            className={`dc-products-scanner-candidate ${selected ? "selected" : ""}`}
                          >
                            <div className="dc-products-product-image">
                              {getProductImage(product) ? (
                                <img
                                  src={getProductImage(product)}
                                  alt={getProductName(product)}
                                  loading="lazy"
                                />
                              ) : (
                                <Package size={25} />
                              )}
                            </div>

                            <div className="dc-products-scanner-candidate-info">
                              <strong>{getProductName(product)}</strong>
                              <span>SKU: {product.sku || "Sin SKU"}</span>
                              <span>{getStoreName(product, stores)}</span>
                              <b>{money(product.price)}</b>
                            </div>

                            <button
                              type="button"
                              className={selected ? "dc-products-primary" : "dc-products-secondary"}
                              onClick={() => selectScannerCandidate(product)}
                            >
                              {selected
                                ? <CheckCircle2 size={16} />
                                : <PackageCheck size={16} />}
                              {selected ? "SELECCIONADO" : "REVISAR"}
                            </button>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* HISTORIAL LOCAL DE ESTA SESIÓN */}

                <div className="dc-products-scanner-history">
                  <div className="dc-products-panel-heading">
                    <div>
                      <span className="dc-products-eyebrow">SESSION HISTORY</span>
                      <h2><History size={20} /> Historial de esta sesión</h2>
                      <p>
                        Este historial permanece en pantalla mientras el componente esté montado.
                        No sustituye el historial persistente de ProductScan en Django.
                      </p>
                    </div>
                  </div>

                  {scanHistory.length === 0 ? (
                    <div className="dc-products-empty compact">
                      <History size={32} />
                      <strong>SIN REVISIONES EN ESTA SESIÓN</strong>
                      <span>Las búsquedas que realices aparecerán aquí.</span>
                    </div>
                  ) : (
                    <div className="dc-products-table-wrap">
                      <table className="dc-products-table">
                        <thead>
                          <tr>
                            <th>FECHA</th>
                            <th>IMAGEN</th>
                            <th>TIENDA</th>
                            <th>BÚSQUEDA</th>
                            <th>RESULTADOS</th>
                            <th>ESTADO</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scanHistory.map((item) => (
                            <tr key={item.id}>
                              <td>{new Date(item.date).toLocaleString("es-CO")}</td>
                              <td>{item.fileName}</td>
                              <td>{item.store}</td>
                              <td>{item.query}</td>
                              <td>{item.results}</td>
                              <td>
                                <span className="dc-products-status active">
                                  {item.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* =================================================
                CATÁLOGO
            ================================================= */}

            {activeTab === "catalog" && (
              <section className="dc-products-panel">
                <div className="dc-products-panel-heading">
                  <div>
                    <span className="dc-products-eyebrow">PRODUCT MANAGEMENT</span>
                    <h2>Catálogo de productos</h2>
                    <p>Consulta, filtra, crea y edita productos registrados en Django.</p>
                  </div>

                  <span className="dc-products-total-badge">
                    {filteredProducts.length} PRODUCTOS
                  </span>
                </div>

                <div className="dc-products-toolbar">
                  <label className="dc-products-search">
                    <Search size={18} />
                    <input
                      type="search"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Buscar nombre, SKU, código o descripción..."
                    />
                    {search && (
                      <button type="button" onClick={() => setSearch("")} aria-label="Limpiar búsqueda">
                        <X size={15} />
                      </button>
                    )}
                  </label>

                  <label className="dc-products-filter">
                    <span>ESTADO</span>
                    <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                      <option value="all">Todos</option>
                      <option value="active">Activos</option>
                      <option value="inactive">Inactivos</option>
                      <option value="featured">Destacados</option>
                    </select>
                    <ChevronDown size={15} />
                  </label>

                  <label className="dc-products-filter">
                    <span>TIENDA</span>
                    <select value={storeFilter} onChange={(event) => setStoreFilter(event.target.value)}>
                      <option value="all">Todas</option>
                      {stores.map((store) => {
                        const storeId = store.id ?? store.pk;
                        const storeName =
                          store.name ||
                          store.store_name ||
                          store.business_name ||
                          store.title ||
                          `Tienda #${storeId}`;

                        return (
                          <option key={String(storeId)} value={String(storeId)}>
                            {storeName}
                          </option>
                        );
                      })}
                    </select>
                    <ChevronDown size={15} />
                  </label>

                  <label className="dc-products-filter">
                    <span>CATEGORÍA</span>
                    <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
                      <option value="all">Todas</option>
                      {categories.map((category) => (
                        <option key={category.id} value={String(category.id)}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={15} />
                  </label>
                </div>

                {filteredProducts.length === 0 ? (
                  <div className="dc-products-empty">
                    <PackageSearchIcon />
                    <strong>NO HAY PRODUCTOS PARA MOSTRAR</strong>
                    <span>Comprueba los filtros o registra un producto nuevo.</span>
                    <button type="button" className="dc-products-primary" onClick={openCreateForm}>
                      <Plus size={17} /> CREAR PRODUCTO
                    </button>
                  </div>
                ) : (
                  <div className="dc-products-table-wrap">
                    <table className="dc-products-table">
                      <thead>
                        <tr>
                          <th>PRODUCTO</th>
                          <th>TIENDA / CATEGORÍA</th>
                          <th>PRECIO</th>
                          <th>INVENTARIO</th>
                          <th>ESTADO</th>
                          <th>ACCIONES</th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredProducts.map((product) => {
                          const productId = getProductId(product);
                          const stock = getStockValues(product, inventory);
                          const photo = getProductImage(product);

                          return (
                            <tr key={productId}>
                              <td>
                                <div className="dc-products-product-cell">
                                  <div className="dc-products-product-image">
                                    {photo
                                      ? <img src={photo} alt={getProductName(product)} loading="lazy" />
                                      : <Package size={24} />}
                                  </div>

                                  <div className="dc-products-product-info">
                                    <strong>{getProductName(product)}</strong>
                                    <span>SKU: {product.sku || "PENDIENTE"}</span>
                                    <small>
                                      ID: {productId ?? "—"}
                                      {product.is_featured ? " · DESTACADO" : ""}
                                    </small>
                                  </div>
                                </div>
                              </td>

                              <td>
                                <div className="dc-products-store-category">
                                  <span><Store size={13} /> {getStoreName(product, stores)}</span>
                                  <span><Tag size={13} /> {getCategoryName(product, categories)}</span>
                                </div>
                              </td>

                              <td>
                                <div className="dc-products-price-cell">
                                  <strong>{money(product.price)}</strong>
                                  {Number(product.compare_price || 0) > Number(product.price || 0) && (
                                    <del>{money(product.compare_price)}</del>
                                  )}
                                  {product.profit_percentage !== undefined && (
                                    <small>Margen: {number(product.profit_percentage)}%</small>
                                  )}
                                </div>
                              </td>

                              <td>
                                <div className="dc-products-stock-cell">
                                  <strong>{number(stock.available)}</strong>
                                  <span>
                                    {number(stock.quantity)} unidades · {number(stock.reserved)} reservadas
                                  </span>
                                  {stock.empty
                                    ? <small className="dc-products-stock-empty">AGOTADO</small>
                                    : stock.low
                                      ? <small className="dc-products-stock-low">STOCK BAJO</small>
                                      : <small className="dc-products-stock-good">DISPONIBLE</small>}
                                </div>
                              </td>

                              <td>
                                <div className="dc-products-status-stack">
                                  <span className={`dc-products-status ${product.is_active ? "active" : "inactive"}`}>
                                    {product.is_active ? <CheckCircle2 size={13} /> : <EyeOff size={13} />}
                                    {product.is_active ? "ACTIVO" : "INACTIVO"}
                                  </span>
                                  {product.is_featured && (
                                    <span className="dc-products-featured">
                                      <Sparkles size={12} /> DESTACADO
                                    </span>
                                  )}
                                </div>
                              </td>

                              <td>
                                <div className="dc-products-actions">
                                  <button
                                    type="button"
                                    className="dc-products-icon-button"
                                    title="Ver detalle"
                                    aria-label={`Ver ${getProductName(product)}`}
                                    onClick={() => setSelectedProduct(product)}
                                  >
                                    <Eye size={16} />
                                  </button>

                                  <button
                                    type="button"
                                    className="dc-products-icon-button edit"
                                    title="Editar producto"
                                    aria-label={`Editar ${getProductName(product)}`}
                                    onClick={() => openEditForm(product)}
                                  >
                                    <Edit3 size={16} />
                                  </button>

                                  <button
                                    type="button"
                                    className="dc-products-icon-button toggle"
                                    title={product.is_active ? "Desactivar" : "Activar"}
                                    aria-label={product.is_active ? "Desactivar producto" : "Activar producto"}
                                    disabled={changingStatus === productId}
                                    onClick={() => toggleProductStatus(product)}
                                  >
                                    {changingStatus === productId
                                      ? <LoaderCircle size={16} className="dc-products-spin" />
                                      : product.is_active
                                        ? <EyeOff size={16} />
                                        : <CheckCircle2 size={16} />}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {/* =================================================
                FORMULARIO
            ================================================= */}

            {activeTab === "form" && showForm && (
              <section className="dc-products-panel dc-products-form-panel">
                <div className="dc-products-panel-heading">
                  <div>
                    <span className="dc-products-eyebrow">
                      {editingProduct ? "PRODUCT EDITOR" : "PRODUCT CREATOR"}
                    </span>
                    <h2>{editingProduct ? "Editar producto" : "Registrar producto"}</h2>
                    <p>Los precios definitivos y sus validaciones corresponden al backend.</p>
                  </div>

                  <button
                    type="button"
                    className="dc-products-secondary"
                    onClick={closeForm}
                    disabled={saving}
                  >
                    <X size={16} /> CERRAR
                  </button>
                </div>

                <form className="dc-products-form" onSubmit={saveProduct}>
                  <div className="dc-products-form-section">
                    <div className="dc-products-form-section-title">
                      <Package size={18} />
                      <div>
                        <h3>Información del producto</h3>
                        <p>Identificación y organización del catálogo.</p>
                      </div>
                    </div>

                    <div className="dc-products-form-grid">
                      <label className="dc-products-field">
                        <span>NOMBRE DEL PRODUCTO *</span>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(event) => setForm((previous) => ({ ...previous, name: event.target.value }))}
                          placeholder="Ej. Producto premium"
                          maxLength={180}
                          required
                        />
                      </label>

                      <label className="dc-products-field">
                        <span>SKU</span>
                        <input
                          type="text"
                          value={form.sku}
                          onChange={(event) => setForm((previous) => ({ ...previous, sku: event.target.value }))}
                          placeholder="Automático si lo genera el backend"
                        />
                      </label>

                      <label className="dc-products-field">
                        <span>TIENDA *</span>
                        <select
                          value={form.store}
                          onChange={(event) => setForm((previous) => ({ ...previous, store: event.target.value }))}
                          required
                        >
                          <option value="">Seleccionar tienda</option>
                          {stores.map((store) => {
                            const storeId = store.id ?? store.pk;
                            const storeName =
                              store.name ||
                              store.store_name ||
                              store.business_name ||
                              store.title ||
                              `Tienda #${storeId}`;

                            return (
                              <option key={String(storeId)} value={String(storeId)}>
                                {storeName}
                              </option>
                            );
                          })}
                        </select>
                      </label>

                      <label className="dc-products-field">
                        <span>CATEGORÍA</span>
                        <select
                          value={form.category}
                          onChange={(event) => setForm((previous) => ({ ...previous, category: event.target.value }))}
                        >
                          <option value="">Sin categoría</option>
                          {categories.map((category) => (
                            <option key={category.id} value={String(category.id)}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="dc-products-field full">
                        <span>DESCRIPCIÓN</span>
                        <textarea
                          value={form.description}
                          onChange={(event) => setForm((previous) => ({ ...previous, description: event.target.value }))}
                          rows={5}
                          placeholder="Describe el producto, sus características y detalles..."
                        />
                      </label>
                    </div>
                  </div>

                  <div className="dc-products-form-section">
                    <div className="dc-products-form-section-title">
                      <Activity size={18} />
                      <div>
                        <h3>Configuración comercial</h3>
                        <p>Costos, margen y precio calculado.</p>
                      </div>
                    </div>

                    <div className="dc-products-form-grid">
                      <label className="dc-products-field">
                        <span>COSTO DE COMPRA (COP) *</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={form.cost_price}
                          onChange={(event) => setForm((previous) => ({ ...previous, cost_price: event.target.value }))}
                          placeholder="0"
                          required
                        />
                      </label>

                      <label className="dc-products-field">
                        <span>MARGEN DE GANANCIA (%) *</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.profit_percentage}
                          onChange={(event) => setForm((previous) => ({ ...previous, profit_percentage: event.target.value }))}
                          required
                        />
                      </label>

                      <div className="dc-products-calculated-price">
                        <span>PRECIO ESTIMADO</span>
                        <strong>{money(calculatedPrice)}</strong>
                        <small>Vista previa; Django calcula el precio definitivo.</small>
                      </div>

                      <label className="dc-products-field">
                        <span>PRECIO DE COMPARACIÓN (COP)</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={form.compare_price}
                          onChange={(event) => setForm((previous) => ({ ...previous, compare_price: event.target.value }))}
                          placeholder="Opcional"
                        />
                        <small>Precio anterior o de referencia para promociones.</small>
                      </label>
                    </div>
                  </div>

                  <div className="dc-products-form-section">
                    <div className="dc-products-form-section-title">
                      <ImagePlus size={18} />
                      <div>
                        <h3>Imágenes del producto</h3>
                        <p>La fotografía original y la imagen comercial cumplen funciones diferentes.</p>
                      </div>
                    </div>

                    <div className="dc-products-images-grid">
                      <div className="dc-products-image-editor">
                        <div className="dc-products-image-editor-heading">
                          <strong>FOTOGRAFÍA ORIGINAL</strong>
                          <span>Referencia interna / escaneo</span>
                        </div>

                        <button
                          type="button"
                          className="dc-products-image-upload"
                          onClick={() => originalInputRef.current?.click()}
                        >
                          {previews.original_image
                            ? <img src={previews.original_image} alt="Fotografía original" />
                            : <>
                                <Upload size={27} />
                                <strong>SUBIR FOTO ORIGINAL</strong>
                                <span>JPG, PNG o WEBP · Máximo 10 MB</span>
                              </>}
                        </button>

                        <input
                          ref={originalInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="dc-products-hidden-input"
                          onChange={(event) => handleImageChange(event, "original_image")}
                        />

                        <small className="dc-products-image-note">
                          No debe mostrarse públicamente salvo que el backend lo permita.
                        </small>
                      </div>

                      <div className="dc-products-image-editor">
                        <div className="dc-products-image-editor-heading">
                          <strong>IMAGEN DEL CATÁLOGO</strong>
                          <span>Imagen principal para clientes</span>
                        </div>

                        <button
                          type="button"
                          className="dc-products-image-upload"
                          onClick={() => catalogInputRef.current?.click()}
                        >
                          {previews.image
                            ? <img src={previews.image} alt="Imagen principal del catálogo" />
                            : <>
                                <ImagePlus size={27} />
                                <strong>SUBIR IMAGEN PRINCIPAL</strong>
                                <span>JPG, PNG o WEBP · Máximo 10 MB</span>
                              </>}
                        </button>

                        <input
                          ref={catalogInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="dc-products-hidden-input"
                          onChange={(event) => handleImageChange(event, "image")}
                        />

                        <small className="dc-products-image-note">
                          Se guarda en el campo de imagen principal del producto.
                        </small>
                      </div>
                    </div>

                    <div className="dc-products-gallery-note">
                      <Sparkles size={17} />
                      <span>
                        La galería adicional se lee desde las imágenes relacionadas que devuelva el backend.
                      </span>
                    </div>
                  </div>

                  <div className="dc-products-form-section">
                    <div className="dc-products-form-section-title">
                      <ShieldCheck size={18} />
                      <div>
                        <h3>Publicación y visibilidad</h3>
                        <p>Define cómo se administra el producto en el catálogo.</p>
                      </div>
                    </div>

                    <div className="dc-products-toggles">
                      <label className="dc-products-toggle">
                        <input
                          type="checkbox"
                          checked={form.is_active}
                          onChange={(event) => setForm((previous) => ({ ...previous, is_active: event.target.checked }))}
                        />
                        <span className="dc-products-toggle-ui" />
                        <span className="dc-products-toggle-copy">
                          <strong>PRODUCTO ACTIVO</strong>
                          <small>Habilita el producto según las reglas del backend.</small>
                        </span>
                      </label>

                      <label className="dc-products-toggle">
                        <input
                          type="checkbox"
                          checked={form.is_featured}
                          onChange={(event) => setForm((previous) => ({ ...previous, is_featured: event.target.checked }))}
                        />
                        <span className="dc-products-toggle-ui" />
                        <span className="dc-products-toggle-copy">
                          <strong>PRODUCTO DESTACADO</strong>
                          <small>Marca el producto para secciones destacadas.</small>
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="dc-products-form-actions">
                    <button type="button" className="dc-products-secondary" onClick={closeForm} disabled={saving}>
                      CANCELAR
                    </button>

                    <button type="submit" className="dc-products-primary" disabled={saving}>
                      {saving
                        ? <LoaderCircle size={18} className="dc-products-spin" />
                        : <CheckCircle2 size={18} />}
                      {saving
                        ? "GUARDANDO..."
                        : editingProduct
                          ? "GUARDAR CAMBIOS"
                          : "CREAR PRODUCTO"}
                      {!saving && <ArrowRight size={17} />}
                    </button>
                  </div>
                </form>
              </section>
            )}

            {/* =================================================
                INVENTARIO: SOLO LECTURA
            ================================================= */}

            {activeTab === "inventory" && (
              <section className="dc-products-panel">
                <div className="dc-products-panel-heading">
                  <div>
                    <span className="dc-products-eyebrow">INVENTORY LINK</span>
                    <h2>Existencias relacionadas</h2>
                    <p>Información de inventario consultada; no se modifica desde este formulario.</p>
                  </div>

                  <button type="button" className="dc-products-secondary" onClick={loadInventory}>
                    <RefreshCw size={16} /> ACTUALIZAR
                  </button>
                </div>

                {products.length === 0 ? (
                  <div className="dc-products-empty">
                    <Warehouse size={40} />
                    <strong>NO HAY PRODUCTOS REGISTRADOS</strong>
                    <span>Primero registra productos en el catálogo.</span>
                  </div>
                ) : (
                  <div className="dc-products-table-wrap">
                    <table className="dc-products-table">
                      <thead>
                        <tr>
                          <th>PRODUCTO</th>
                          <th>TIENDA</th>
                          <th>EXISTENCIA</th>
                          <th>RESERVADO</th>
                          <th>DISPONIBLE</th>
                          <th>ESTADO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((product) => {
                          const stock = getStockValues(product, inventory);

                          return (
                            <tr key={getProductId(product)}>
                              <td>
                                <div className="dc-products-product-cell">
                                  <div className="dc-products-product-image">
                                    {getProductImage(product)
                                      ? <img src={getProductImage(product)} alt={getProductName(product)} loading="lazy" />
                                      : <Package size={22} />}
                                  </div>
                                  <div className="dc-products-product-info">
                                    <strong>{getProductName(product)}</strong>
                                    <span>SKU: {product.sku || "SIN SKU"}</span>
                                  </div>
                                </div>
                              </td>

                              <td>{getStoreName(product, stores)}</td>
                              <td>{number(stock.quantity)}</td>
                              <td>{number(stock.reserved)}</td>
                              <td><strong>{number(stock.available)}</strong></td>

                              <td>
                                {stock.empty
                                  ? <span className="dc-products-status inactive"><XCircle size={13} /> AGOTADO</span>
                                  : stock.low
                                    ? <span className="dc-products-status low"><AlertTriangle size={13} /> STOCK BAJO</span>
                                    : <span className="dc-products-status active"><PackageCheck size={13} /> DISPONIBLE</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {inventory.length === 0 && (
                  <div className="dc-products-inline-warning">
                    <AlertTriangle size={17} />
                    <span>
                      El endpoint de inventario no devolvió registros. Las cantidades mostradas
                      como cero no significan necesariamente que no existan existencias: verifica
                      la respuesta de tu API.
                    </span>
                  </div>
                )}
              </section>
            )}
          </>
        )}

        <footer className="dc-products-footer">
          <div>
            <ShieldCheck size={17} />
            <span>PRODUCTOS CENTRALIZADOS · DEBUTS CROP</span>
          </div>
          <span>PRECIOS E INVENTARIO VALIDADOS EN EL SERVIDOR</span>
        </footer>
      </main>

      {/* =====================================================
          MODAL DE CÁMARA
      ===================================================== */}

      {cameraOpen && (
        <div className="dc-products-modal-backdrop" role="presentation">
          <section
            className="dc-products-modal dc-products-camera-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dc-camera-title"
          >
            <header className="dc-products-modal-header">
              <div>
                <span className="dc-products-eyebrow">LIVE CAPTURE</span>
                <h2 id="dc-camera-title">Capturar producto</h2>
              </div>

              <button type="button" onClick={stopCamera} aria-label="Cerrar cámara">
                <X size={21} />
              </button>
            </header>

            <div className="dc-products-camera-frame">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                aria-label="Vista de cámara"
              />
            </div>

            <p className="dc-products-image-note">
              Coloca la etiqueta o el producto dentro del encuadre y captura la fotografía.
            </p>

            <div className="dc-products-modal-actions">
              <button type="button" className="dc-products-secondary" onClick={stopCamera}>
                CANCELAR
              </button>
              <button type="button" className="dc-products-primary" onClick={captureCameraImage}>
                <Camera size={17} /> CAPTURAR FOTO
              </button>
            </div>
          </section>
        </div>
      )}

      {/* =====================================================
          MODAL DETALLE
      ===================================================== */}

      {selectedProduct && (
        <div
          className="dc-products-modal-backdrop"
          onMouseDown={() => setSelectedProduct(null)}
        >
          <section
            className="dc-products-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dc-product-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="dc-products-modal-header">
              <div>
                <span className="dc-products-eyebrow">PRODUCT DETAIL</span>
                <h2 id="dc-product-modal-title">{getProductName(selectedProduct)}</h2>
              </div>
              <button type="button" onClick={() => setSelectedProduct(null)} aria-label="Cerrar detalle">
                <X size={21} />
              </button>
            </header>

            <div className="dc-products-detail-hero">
              <div className="dc-products-detail-image">
                {getProductImage(selectedProduct)
                  ? <img src={getProductImage(selectedProduct)} alt={getProductName(selectedProduct)} />
                  : <Package size={38} />}
              </div>

              <div className="dc-products-detail-main">
                <span className="dc-products-eyebrow">
                  SKU · {selectedProduct.sku || "SIN SKU"}
                </span>
                <strong>{money(selectedProduct.price)}</strong>
                <span>{getStoreName(selectedProduct, stores)}</span>
                <span>{getCategoryName(selectedProduct, categories)}</span>

                <div className="dc-products-detail-badges">
                  <span className={`dc-products-status ${selectedProduct.is_active ? "active" : "inactive"}`}>
                    {selectedProduct.is_active ? "ACTIVO" : "INACTIVO"}
                  </span>
                  {selectedProduct.is_featured && (
                    <span className="dc-products-featured"><Sparkles size={12} /> DESTACADO</span>
                  )}
                </div>
              </div>
            </div>

            <div className="dc-products-detail-grid">
              <div><span>ID DEL PRODUCTO</span><strong>{getProductId(selectedProduct) || "—"}</strong></div>
              <div><span>PRECIO DE COMPARACIÓN</span><strong>{money(selectedProduct.compare_price)}</strong></div>
              <div><span>COSTO</span><strong>{money(selectedProduct.cost_price)}</strong></div>
              <div><span>MARGEN</span><strong>{number(selectedProduct.profit_percentage)}%</strong></div>
              <div><span>VISTAS</span><strong>{number(selectedProduct.views)}</strong></div>
              <div><span>VENTAS</span><strong>{number(selectedProduct.sales_count)}</strong></div>
              <div><span>SELECCIONES</span><strong>{number(selectedProduct.selection_count)}</strong></div>
              <div>
                <span>FECHA DE CREACIÓN</span>
                <strong>
                  {selectedProduct.created_at
                    ? new Date(selectedProduct.created_at).toLocaleDateString("es-CO")
                    : "—"}
                </strong>
              </div>
            </div>

            {selectedProduct.description && (
              <div className="dc-products-detail-description">
                <span>DESCRIPCIÓN</span>
                <p>{selectedProduct.description}</p>
              </div>
            )}

            {selectedProduct.original_image && (
              <div className="dc-products-detail-original">
                <span>FOTOGRAFÍA ORIGINAL</span>
                <img src={imageUrl(selectedProduct.original_image)} alt="Fotografía original del producto" />
              </div>
            )}

            {(selectedProduct.catalog_images?.length > 0 ||
              selectedProduct.catalog_gallery?.length > 0) && (
              <div className="dc-products-detail-gallery">
                <span>GALERÍA DEL CATÁLOGO</span>
                <div className="dc-products-gallery-grid">
                  {(selectedProduct.catalog_images || selectedProduct.catalog_gallery || []).map((image, index) => (
                    <div key={image.id || index}>
                      {imageUrl(image.image || image.url)
                        ? <img src={imageUrl(image.image || image.url)} alt={`Imagen del catálogo ${index + 1}`} />
                        : <ImagePlus size={22} />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="dc-products-modal-actions">
              <button type="button" className="dc-products-secondary" onClick={() => setSelectedProduct(null)}>
                CERRAR
              </button>
              <button
                type="button"
                className="dc-products-primary"
                onClick={() => {
                  const product = selectedProduct;
                  setSelectedProduct(null);
                  openEditForm(product);
                }}
              >
                <Edit3 size={17} /> EDITAR PRODUCTO
              </button>
            </div>
          </section>
        </div>
      )}
    </ProfilePageLayout>
  );
}

// =========================================================
// ICONO AUXILIAR
// =========================================================

function PackageSearchIcon() {
  return <PackageSearch size={43} strokeWidth={1.4} />;
}
